/** @jsxRuntime classic */
/** @jsx jsx */
import { forwardRef, memo, useEffect, useState, useRef, useImperativeHandle } from 'react';
import { jsx, css } from '@emotion/core';
import { randomString, getFileType } from "../../utils";
import PropTypes from 'prop-types';
import CircularProgress from "../progress/circular";
import { animations } from "../../styles/animation";
import { displayBlock, displayNone, justifyCenter, positionAbsolute } from "../../styles/general";
const fadeIn = animations.fadeIn;
const regex = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g;
const googleDriveRegex = /(http(s)?:\/\/.)?(drive\.google\.com\/file\/d\/)/;
const googleDocsRegex = /(http(s)?:\/\/.)?(docs\.google\.com\/fileview\?)/;
const checkURL = link => {
  const res = regex.test(link);
  if (res) {
    if (googleDriveRegex.test(link)) {
      const id = link.split('/')[0] === 'drive.google.com' ? link.split('/')[3] : link.split('/')[5];
      return `https://docs.google.com/viewer?srcid=${id}&pid=explorer&efh=false&a=v&chrome=false&embedded=true`;
    }
    if (googleDocsRegex.test(link)) {
      return `https://docs.google.com/viewer?srcid=${link.split('=')[1]}&pid=explorer&efh=false&a=v&chrome=false&embedded=true`;
    }
  }
  return link;
};
const SliderItem = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  active,
  animation,
  fileName,
  url,
  ...props
}, reference) => {
  const Id = randomString(7, {
    allowSymbol: false,
    allowNumber: false
  });
  const IDs = {
    frame: 'DGN-UI-Slider-frame' + Id,
    image: 'DGN-UI-Slider-image' + Id,
    itemContainer: 'DGN-UI-Slider-item-container-' + Id,
    video: 'DGN-UI-Slider-video' + Id
  };
  const ref = useRef(null);
  const iframeRef = useRef(null);
  const [iframeTimeoutId, setIframeTimeoutId] = useState(null);
  const getViewer = (filePath, fileType) => {
    switch (fileType) {
      case 'image':
        {
          return jsx("img", {
            ...props,
            className: IDs.image,
            css: imageCSS,
            src: filePath,
            alt: ''
          });
        }
      case 'word':
      case 'sheet':
        {
          return jsx("iframe", {
            ...props,
            className: IDs.frame,
            css: frameCSS,
            frameBorder: 0,
            src: `https://view.officeapps.live.com/op/view.aspx?src=${filePath}`,
            title: fileName
          });
        }
      case 'pdf':
        {
          return jsx("iframe", {
            ...props,
            ref: iframeRef,
            className: IDs.frame,
            css: frameCSS,
            frameBorder: 0,
            src: getIframeLink(),
            title: fileName,
            onLoad: iframeLoaded,
            onError: updateIframeSrc
          });
        }
      case 'mp4':
        {
          return jsx("video", {
            autoPlay: true,
            className: IDs.video,
            controls: "controls",
            height: "auto",
            width: "auto"
          }, jsx("source", {
            src: filePath,
            type: "video/mp4"
          }));
        }
      case 'another':
        {
          return jsx("iframe", {
            ...props,
            className: IDs.frame,
            css: frameCSS,
            frameBorder: 0,
            src: checkURL(filePath),
            title: fileName
          });
        }
      default:
        {
          return jsx("iframe", {
            ...props,
            className: IDs.frame,
            css: frameCSS,
            frameBorder: 0,
            src: filePath,
            title: fileName
          });
        }
    }
  };
  useEffect(() => {
    const fileType = getFileType(url, undefined, false);
    if (fileType === 'pdf') {
      const intervalId = setInterval(updateIframeSrc, 2000);
      setIframeTimeoutId(intervalId);
    }
  }, []);
  const iframeLoaded = () => {
    clearInterval(iframeTimeoutId);
    setIframeTimeoutId(null);
  };
  const getIframeLink = () => {
    return `https://docs.google.com/gview?url=${url}&embedded=true`;
  };
  const updateIframeSrc = () => {
    if (iframeRef.current) {
      iframeRef.current.src = getIframeLink();
    }
  };
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    const _instance = {}; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return jsx("div", {
    className: [IDs.itemContainer, active ? 'active' : '', animation ? 'item-animation' : ''].join(' ').trim().replace(/\s+/g, ' '),
    css: itemContainerCSS,
    id: IDs.itemContainer,
    ref: ref
  }, iframeTimeoutId && getFileType(url, undefined, false) === 'pdf' && jsx("div", {
    css: loadingCSS
  }, jsx(CircularProgress, {
    size: 'xs'
  })), getViewer(url, getFileType(url, undefined, false)));
}));
const itemContainerCSS = css`
    ${displayNone};
    ${justifyCenter};
    height: 100%;
    width: 100%;
    &.item-animation {
        animation: ${fadeIn} 1s;
    }
`;
const frameCSS = css`
    ${displayBlock};
    height: 85vh;
    margin: 0;
    padding: 0;
    width: 100%;
`;
const imageCSS = css`
    ${displayBlock};
    height: auto;
    margin: auto;
    max-height: 100%;
    max-width: 100%;
    width: auto;
`;
const loadingCSS = css`
    ${positionAbsolute};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;
SliderItem.defaultProps = {
  active: false,
  animation: true
};
SliderItem.propTypes = {
  active: PropTypes.bool,
  animation: PropTypes.bool,
  fileName: PropTypes.string,
  url: PropTypes.string
};
export default SliderItem;